/**
 * Role-based Access Control Component
 * Controls access to routes based on user roles
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: string[];
  fallbackPath?: string;
}

/**
 * Role Guard Component
 * 
 * @example
 * <RoleGuard allowedRoles={['admin']}>
 *   <AdminOnlyComponent />
 * </RoleGuard>
 */
const RoleGuard: React.FC<RoleGuardProps> = ({ 
  children, 
  allowedRoles, 
  fallbackPath = '/campaign-list' 
}) => {
  const { user } = useAuth();

  // If no user, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Get user roles from the user object
  // Assuming user has roles array or we need to extract from the stored user data
  const userRoles = user.roles || [];
  
  // Check if user has any of the allowed roles
  const hasPermission = allowedRoles.some(role => 
    userRoles.some(userRole => 
      userRole.toLowerCase() === role.toLowerCase()
    )
  );

  if (!hasPermission) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export default RoleGuard;
